import {useCallback, useEffect} from 'react';
import {mergeClasses, makeStyles} from '@fluentui/react-components';
import {outputTypesFetchIfNeeded} from 'actions/refTables/outputTypes.jsx';
import {HorizontalLoader} from 'components/shared';
import { FormattedMessage, useIntl } from 'react-intl';
import { requestMessages } from './requestMessages';
import {
    fundOutputAddNodes,
    fundOutputDetailFetchIfNeeded,
    fundOutputEdit,
    fundOutputRemoveNodes,
} from '../../actions/arr/fundOutput.jsx';
import {descItemTypesFetchIfNeeded} from 'actions/refTables/descItemTypes.jsx';
import {refRulDataTypesFetchIfNeeded} from 'actions/refTables/rulDataTypes.jsx';
import {modalDialogShow} from 'actions/global/modalDialog.jsx';
import './ArrOutputDetail.scss';
import {OutputEdit} from './output/OutputEdit';
import {OutputColumnLayout} from './output/OutputColumnLayout';
import {OutputStackedLayout} from './output/OutputStackedLayout';
import FundNodesSelectForm from './FundNodesSelectForm';
import {FundOutputFiles} from './output/FundOutputFiles';
import {ApScopeVO, ArrOutputVO} from '../../typings/Outputs';
import {AppFetchingStore} from '../../typings/globals';
import * as scopeActions from '../../actions/scopes/scopes';
import storeFromArea from '../../shared/utils/storeFromArea';
import {WebApi} from 'actions/index';
import {showConfirmDialog} from 'components/shared/dialog';
import {useAppThunkDispatch, useContainerWidth} from 'utils/hooks';
import {useAppSelector} from 'utils/hooks/useAppSelector';
import {useUserSettings} from 'contexts/user/useSettings';

const COLUMN_LAYOUT_MIN_WIDTH = 900;

const useStyles = makeStyles({
    columnContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
});

const OutputState = {
    OPEN: 'OPEN',
    COMPUTING: 'COMPUTING',
    GENERATING: 'GENERATING',
    FINISHED: 'FINISHED',
    OUTDATED: 'OUTDATED',
    ERROR: 'ERROR', /// Pomocný stav websocketu
};

interface Props {
    versionId: number;
    fund: any;
    descItemTypes: any;
    templates: any;
    outputFilters: any;
    rulDataTypes: any;
    closed: boolean;
    readMode: boolean;
    fundOutputDetail: ArrOutputVO & AppFetchingStore & {lockDate: any};
}

const isOutputEditable = (item: Props['fundOutputDetail']) => {
    return !item.lockDate && item.state === OutputState.OPEN;
};

/**
 * Formulář detailu a editace verze výstupu.
 */
export function ArrOutputDetail({
    versionId,
    fund,
    descItemTypes,
    outputFilters,
    closed,
    readMode,
    fundOutputDetail,
}: Props) {
    const dispatch = useAppThunkDispatch();
    const intl = useIntl();
    const styles = useStyles();
    const {settings} = useUserSettings();
    const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
    const scopeList = useAppSelector(state => storeFromArea(state, scopeActions.AREA_SCOPE_LIST));

    useEffect(() => {
        dispatch(descItemTypesFetchIfNeeded());
        dispatch(refRulDataTypesFetchIfNeeded());
    }, [dispatch, versionId, fundOutputDetail.fetched, fundOutputDetail.currentDataKey]);

    useEffect(() => {
        if (fundOutputDetail.id !== null) {
            dispatch(fundOutputDetailFetchIfNeeded(versionId, fundOutputDetail.id));
        }
        dispatch(outputTypesFetchIfNeeded());
        dispatch(scopeActions.scopesListFetchIfNeeded());
    }, [dispatch, versionId, fundOutputDetail.id, fundOutputDetail.currentDataKey]);

    const handleSaveOutput = useCallback(
        (data: Partial<ArrOutputVO>) => {
            return dispatch(fundOutputEdit(fund.versionId, fundOutputDetail.id, data));
        },
        [dispatch, fund.versionId, fundOutputDetail.id],
    );

    const handleRemoveNode = useCallback(
        async (node: any) => {
            const response = (await dispatch(showConfirmDialog(intl.formatMessage(requestMessages.fundNodesDeleteNode)))) as any;
            if (response) {
                dispatch(fundOutputRemoveNodes(fund.versionId, fundOutputDetail.id, [node.id]));
            }
        },
        [dispatch, fund.versionId, fundOutputDetail.id],
    );

    const handleRemoveScope = useCallback(
        async (scope: ApScopeVO) => {
            const response = (await dispatch(showConfirmDialog(intl.formatMessage(requestMessages.fundNodesDeleteNode)))) as any;
            if (response) {
                WebApi.deleteRestrictedScope(fundOutputDetail.id, scope.id);
            }
        },
        [dispatch, fundOutputDetail.id],
    );

    const handleAddScope = useCallback(
        (scope: ApScopeVO) => {
            WebApi.addRestrictedScope(fundOutputDetail.id, scope.id);
            // Zbytek zařídí websocket
        },
        [fundOutputDetail.id],
    );

    const handleAddNodes = useCallback(() => {
        dispatch(
            modalDialogShow(
                null,
                intl.formatMessage(requestMessages.fundNodesTitleSelect),
                <FundNodesSelectForm
                    // @ts-ignore
                    onSubmitForm={(ids, nodes) => {
                        dispatch(fundOutputAddNodes(fund.versionId, fundOutputDetail.id, ids));
                    }}
                />,
            ),
        );
    }, [dispatch, fund.versionId, fundOutputDetail.id]);

    const renderOutputFiles = () => {
        if (fundOutputDetail.outputResultIds === null || fundOutputDetail.outputResultIds.length === 0) {
            return null;
        }

        return (
            <FundOutputFiles
                versionId={versionId}
                outputId={fundOutputDetail.id}
                outputResultIds={fundOutputDetail.outputResultIds}
            />
        );
    };

    if (fundOutputDetail.id === null) {
        return (
            <div className="arr-output-detail-container">
                <div className="unselected-msg">
                    <div className="title">{<FormattedMessage {...requestMessages.outputNoSelectionTitle} />}</div>
                    <div className="msg-text">{<FormattedMessage {...requestMessages.outputNoSelectionMessage} />}</div>
                </div>
            </div>
        );
    }

    const fetched =
        fundOutputDetail.fetched &&
        outputFilters.fetched &&
        descItemTypes.fetched;
    if (!fetched) {
        return <HorizontalLoader />;
    }

    const readonly = closed || readMode || !isOutputEditable(fundOutputDetail);

    const form = <OutputEdit outputId={fundOutputDetail.id} readonly={readonly} />;

    const existingScopes = (fundOutputDetail.scopes || []).map(i => i.id);
    const connectableScopes = scopeList.rows && scopeList.rows.filter(s => existingScopes.indexOf(s.id) === -1);

    const fitsColumns = containerWidth === null || containerWidth >= COLUMN_LAYOUT_MIN_WIDTH;
    const useColumns = !!settings.outputColumnLayout && fitsColumns;

    const layoutProps = {
        fundOutputDetail,
        versionId,
        readonly,
        nodesReadOnly: readonly,
        connectableScopes,
        form,
        outputFiles: renderOutputFiles(),
        onSaveOutput: handleSaveOutput,
        onAddScope: handleAddScope,
        onRemoveScope: handleRemoveScope,
        onAddNodes: handleAddNodes,
        onRemoveNode: handleRemoveNode,
    };

    return (
        <div
            ref={containerRef}
            className={mergeClasses('arr-output-detail-container', useColumns && styles.columnContainer)}
        >
            {useColumns ? <OutputColumnLayout {...layoutProps} /> : <OutputStackedLayout {...layoutProps} />}
        </div>
    );
}

export default ArrOutputDetail;
